// C:\Users\ADMIN\Desktop\ERP\myerp\my-frontend\src\pages\etc\ExpenseUsageCodes.jsx
import React, { useEffect, useState, useRef } from "react";
import api from "../../api";
import styles from "./ExpenseUsageCodes.module.css";

export default function ExpenseUsageCodes() {
  const formatCode = (code) => {
    if (!code) return "";
    return String(code).padStart(4, "0");
  };

  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [fields, setFields] = useState([]);

  const [form, setForm] = useState({
    categoryCode: "", categoryName: "",
    itemCode: "", itemName: "",
    fieldCode: "", fieldName: "",
  });

  const [editTarget, setEditTarget] = useState(null);

  const [showSuggest, setShowSuggest] = useState({
    categoryName: false,
    itemName: false,
    fieldName: false,
  });

  const suggestRefs = {
    categoryName: useRef(null),
    itemName: useRef(null),
    fieldName: useRef(null),
  };

  async function loadData() {
    try {
      const [resCat, resItem, resField] = await Promise.all([
        api.get("/expense/usage/category/"),
        api.get("/expense/usage/item/"),
        api.get("/expense/usage/field/"),
      ]);
      
      const asc = (a, b) => a.code - b.code;
      setCategories([...resCat.data].sort(asc));
      setItems([...resItem.data].sort(asc));
      setFields([...resField.data].sort(asc));
      


    } catch (err) {
      console.error("❌ 코드 데이터 불러오기 실패:", err);
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      const clickedOutside = Object.values(suggestRefs).every(
        (ref) => ref.current && !ref.current.contains(e.target)
      );
      if (clickedOutside) {
        setShowSuggest({ categoryName: false, itemName: false, fieldName: false });
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  
    // 이름 입력 시
    if (["categoryName", "itemName", "fieldName"].includes(name)) {
      setShowSuggest((prev) => ({ ...prev, [name]: value.trim().length > 0 }));
    }
  
    // ✅ 코드 입력 시도 드롭다운 열기
    const codeToNameMap = {
      categoryCode: "categoryName",
      itemCode: "itemName",
      fieldCode: "fieldName",
    };
    if (codeToNameMap[name]) {
      setShowSuggest((prev) => ({ ...prev, [codeToNameMap[name]]: value.trim().length > 0 }));
    }
  };


  const getSuggestions = (type, nameQuery, codeQuery = "") => {
    if (!nameQuery.trim() && !codeQuery.trim()) return [];
    const dataMap = { categoryName: categories, itemName: items, fieldName: fields };
    return (dataMap[type] || []).filter((d) => {
      const matchName = nameQuery.trim() && d.name.toLowerCase().includes(nameQuery.toLowerCase());
      const matchCode = codeQuery.trim() && formatCode(d.code).includes(codeQuery);
      return matchName || matchCode;
    });
  };


  const handleSuggestClick = (type, item) => {
    const nameKey = type;
    const codeKey = type.replace("Name", "Code");
    setForm((prev) => ({
      ...prev,
      [codeKey]: String(item.code),
      [nameKey]: item.name,
    }));
    setShowSuggest((prev) => ({ ...prev, [type]: false }));
  };

  const handleSave = async (type) => {
    try {
      if (type === "category") {
        await api.post("/expense/usage/category/", { code: form.categoryCode, name: form.categoryName });
        alert("구분명이 등록되었습니다.");
      } else if (type === "item") {
        await api.post("/expense/usage/item/", { code: form.itemCode, name: form.itemName });
        alert("내용명이 등록되었습니다.");
      } else if (type === "field") {
        await api.post("/expense/usage/field/", { code: form.fieldCode, name: form.fieldName });
        alert("분야명이 등록되었습니다.");
      }
      setForm({ categoryCode: "", categoryName: "", itemCode: "", itemName: "", fieldCode: "", fieldName: "" });
      loadData();
    } catch (err) {
      console.error("❌ 저장 실패:", err);
      alert("저장 중 오류가 발생했습니다.");
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm("삭제하시겠습니까?")) return;
    try {
      const urlMap = { category: "/expense/usage/category/", item: "/expense/usage/item/", field: "/expense/usage/field/" };
      await api.delete(`${urlMap[type]}${id}/`);
      loadData();
    } catch (err) {
      console.error("❌ 삭제 실패:", err);
      alert("삭제 중 오류가 발생했습니다.");
    }
  };

  const handleEditStart = (type, row) => {
    setEditTarget({ type, id: row.id, code: String(row.code), name: row.name });
  };

  const handleEditSave = async () => {
    if (!editTarget) return;
    try {
      const urlMap = { category: "/expense/usage/category/", item: "/expense/usage/item/", field: "/expense/usage/field/" };
      await api.put(`${urlMap[editTarget.type]}${editTarget.id}/`, {
        code: editTarget.code,
        name: editTarget.name,
      });
      setEditTarget(null);
      loadData();
    } catch (err) {
      console.error("❌ 수정 실패:", err);
      alert("수정 중 오류가 발생했습니다.");
    }
  };

  const [search, setSearch] = useState("");

  const filtered = {
    categories: categories.filter(
      (d) =>
        d.name.toLowerCase().includes(search.toLowerCase()) ||
        formatCode(d.code).includes(search)
    ),
    items: items.filter(
      (d) =>
        d.name.toLowerCase().includes(search.toLowerCase()) ||
        formatCode(d.code).includes(search)
    ),
    fields: fields.filter(
      (d) =>
        d.name.toLowerCase().includes(search.toLowerCase()) ||
        formatCode(d.code).includes(search)
    ),
  };

  // ✅ 자동완성 입력 필드 렌더링 — 저장버튼 오른쪽, CODE+이름 한 줄
  const renderInputWithSuggest = ({ label, codeKey, nameKey, placeholder, type }) => {
    const suggestions = getSuggestions(nameKey, form[nameKey], form[codeKey]);

    return (
      <div style={{ marginTop: type !== "category" ? "12px" : 0 }}>
        {/* 라벨 + 저장 버튼 한 줄 */}
        <div className={styles.formHeader}>
          <h4>{label}</h4>
          <button className={styles.btnFormSave} onClick={() => handleSave(type)}>저장</button>
        </div>

        {/* CODE + 이름 입력 한 줄 */}
        <div className={styles.formRow}>
          <input
            type="number"
            name={codeKey}
            placeholder="CODE"
            value={form[codeKey]}
            onChange={handleChange}
            className={styles.codeInput}
          />
          <div ref={suggestRefs[nameKey]} style={{ position: "relative", flex: 1, width: "100%" }}>

            <input
              type="text"
              name={nameKey}
              placeholder={placeholder}
              value={form[nameKey]}
              onChange={handleChange}
              onFocus={() => {
                if (form[nameKey].trim()) {
                  setShowSuggest((prev) => ({ ...prev, [nameKey]: true }));
                }
              }}
              autoComplete="off"
              className={styles.nameInput}
            />
            {showSuggest[nameKey] && suggestions.length > 0 && (
              <ul className={styles.suggestList}>
                {suggestions.map((s) => (
                  <li
                    key={s.id}
                    className={styles.suggestItem}
                    onMouseDown={() => handleSuggestClick(nameKey, s)}
                  >
                    <span className={styles.suggestCode}>{formatCode(s.code)}</span>
                    {s.name}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    );
  };

  const maxLen = Math.max(
    filtered.categories.length,
    filtered.items.length,
    filtered.fields.length
  );

  return (
    <div className={styles.container}>
      <h2>지출 사용내역 코드 관리</h2>

      <div className={styles.contentArea}>
        {/* 왼쪽 입력 폼 */}
        <div className={styles.leftPanel}>

          {/* 검색 영역 */}
          <div className={styles.searchBox}>
            <h4>🔍 코드 검색</h4>
            <input
              type="text"
              placeholder="코드번호 또는 이름으로 검색"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={styles.searchInput}
            />
            {search && (
              <button
                className={styles.searchClear}
                onClick={() => setSearch("")}
              >
                ✕ 초기화
              </button>
            )}
            {search && (
              <p className={styles.searchResult}>
                구분 {filtered.categories.length}건 &nbsp;|&nbsp;
                내용 {filtered.items.length}건 &nbsp;|&nbsp;
                분야 {filtered.fields.length}건
              </p>
            )}
          </div>

          <hr className={styles.divider} />

          {renderInputWithSuggest({
            label: "사용내역 구분명 (0001~0999)",
            codeKey: "categoryCode",
            nameKey: "categoryName",
            placeholder: "구분명 (예: 주재료, 인건비 등)",
            type: "category",
          })}
          {renderInputWithSuggest({
            label: "사용내역 내용명 (1001~1999)",
            codeKey: "itemCode",
            nameKey: "itemName",
            placeholder: "내용명 (예: 우유, 소독약품 등)",
            type: "item",
          })}
          {renderInputWithSuggest({
            label: "사용내역 분야명 (2001~2999)",
            codeKey: "fieldCode",
            nameKey: "fieldName",
            placeholder: "분야명 (예: 구입, 지급, 결제 등)",
            type: "field",
          })}
        </div>

        {/* 오른쪽 코드 테이블 */}
        <div className={styles.rightPanel}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>CODE<br />(0001~0999)</th>
                <th>구분명</th>
                <th>관리</th>
                <th>CODE<br />(1001~1999)</th>
                <th>내용명</th>
                <th>관리</th>
                <th>CODE<br />(2001~2999)</th>
                <th>분야명</th>
                <th>관리</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: maxLen }, (_, i) => {
                const cat = filtered.categories[i];
                const itm = filtered.items[i];
                const fld = filtered.fields[i];

                const renderCell = (type, row) => {
                  if (!row) return <><td>-</td><td>-</td><td></td></>;
                  const isEditing = editTarget?.type === type && editTarget?.id === row.id;
                  return (
                    <>
                      <td>
                        {isEditing ? (
                          <input
                            className={styles.editInput}
                            value={editTarget.code}
                            onChange={(e) => setEditTarget({ ...editTarget, code: e.target.value })}
                          />
                        ) : (
                          formatCode(row.code)
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            className={styles.editInput}
                            value={editTarget.name}
                            onChange={(e) => setEditTarget({ ...editTarget, name: e.target.value })}
                          />
                        ) : (
                          row.name
                        )}
                      </td>
                      {/* ✅ actionWrap으로 감싸야 td 밖으로 안 튀어나옴 */}
                      <td className={styles.actionCell}>
                        <div className={styles.actionWrap}>
                          {isEditing ? (
                            <>
                              <button className={styles.btnSave} onClick={handleEditSave}>완료</button>
                              <button className={styles.btnCancel} onClick={() => setEditTarget(null)}>취소</button>
                            </>
                          ) : (
                            <>
                              <button className={styles.btnEdit} onClick={() => handleEditStart(type, row)}>수정</button>
                              <button className={styles.btnDelete} onClick={() => handleDelete(type, row.id)}>삭제</button>
                            </>
                          )}
                        </div>
                      </td>
                    </>
                  );
                };

                return (
                  <tr key={i}>
                    {renderCell("category", cat)}
                    {renderCell("item", itm)}
                    {renderCell("field", fld)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}